import * as React from 'react';
import { Eye, Users, X } from 'lucide-react';
import {
  AutosaveField,
  PanelSection,
  SidePanel,
  WorkspaceButton,
  WorkspaceNotice,
  useToast,
  workspaceFieldClass,
} from '@/components/ui/workspace';
import { AreaChip } from '@/components/ui/area-chip';
import {
  draftApi,
  type FrameworkArea,
  type FrameworkQuestionnaire,
  type QuestionPatch,
  type QuestionnairePatch,
  type QuestionnaireQuestion,
} from '@/lib/api/discovery-frameworks';
import { cn } from '@/lib/utils/cn';
import { tryAdd, type DraftWrite, type EditorCtx } from './editor';
import {
  ANSWER_TYPES,
  QUESTIONNAIRE_MINUTES,
  answerTypeLabel,
  choicesWith,
  isActive,
  isChoiceType,
  messageOf,
  parseList,
  shown,
} from './framework-logic';
import { ChangeTag, CodeTag, FieldHelp, FlagToggle, InlineAdd, SaveStatus } from './parts';
import { P_TEXT } from './area-panel';

/**
 * Side panel for one questionnaire (prototype `fwQPanel()`): name, who
 * answers it, length, one response per department, and its questions with
 * their codes (Decision 54.3), answer type, area and choices. Preview shows
 * the respondent page.
 */

const SELECT_SM = cn(workspaceFieldClass, 'h-[30px] w-auto pr-7 text-meta');
const ALL = '__all__';
const SEVERAL = '__several__';

export function QuestionnairePanel({
  ctx,
  questionnaire: q,
  onClose,
  onPreview,
}: {
  ctx: EditorCtx;
  questionnaire: FrameworkQuestionnaire;
  onClose: () => void;
  onPreview: () => void;
}) {
  const { content, editing, busy, write } = ctx;
  const { toast } = useToast();
  const retired = q.status === 'retired';
  const canEdit = editing && !retired;
  const patch = (p: QuestionnairePatch) =>
    write((id, base) => draftApi.patchQuestionnaire(id, base, q.id, p), { what: q.code });
  const questions = shown(q.questions, canEdit);
  const activeCount = q.questions.filter(isActive).length;

  return (
    <SidePanel
      aria-label={`Questionnaire ${q.code}`}
      identifier={q.code}
      path={<span className="text-body-sm font-semibold text-ink-900">{q.name}</span>}
      onClose={onClose}
      footer={
        <>
          {editing ? <SaveStatus busy={busy} /> : <span />}
          <div className="flex items-center gap-2">
            {editing ? (
              retired ? (
                <WorkspaceButton disabled={busy} onClick={() => void patch({ status: 'active' })}>
                  Restore questionnaire
                </WorkspaceButton>
              ) : (
                <WorkspaceButton
                  variant="destructive"
                  disabled={busy}
                  onClick={() => void patch({ status: 'retired' })}
                >
                  Retire
                </WorkspaceButton>
              )
            ) : null}
            <WorkspaceButton onClick={onPreview}>
              <Eye className="h-3.5 w-3.5" aria-hidden="true" />
              Preview
            </WorkspaceButton>
          </div>
        </>
      }
    >
      {retired ? (
        <WorkspaceNotice>
          Retired. Once published it is no longer offered for interviews added from then on.
        </WorkspaceNotice>
      ) : null}
      <PanelSection label="Name">
        {canEdit ? (
          <>
            <AutosaveField
              required
              aria-label="Name"
              value={q.name}
              maxLength={120}
              onSave={(name) => void patch({ name })}
            />
            <FieldHelp>Respondents see this as the title.</FieldHelp>
          </>
        ) : (
          <p className={P_TEXT}>{q.name}</p>
        )}
      </PanelSection>
      <PanelSection label="Who answers it">
        {canEdit ? (
          <AutosaveField
            aria-label="Who answers it"
            value={q.audience}
            maxLength={500}
            placeholder="e.g. Payroll Supervisor and HR staff"
            onSave={(audience) => void patch({ audience })}
          />
        ) : (
          <p className="text-body-sm text-ink-900">{q.audience || '—'}</p>
        )}
      </PanelSection>
      <div className="flex items-end gap-3">
        <PanelSection label="About">
          {canEdit ? (
            <select
              aria-label="About how many minutes"
              value={q.minutes}
              disabled={busy}
              onChange={(e) => void patch({ minutes: Number(e.target.value) })}
              className={SELECT_SM}
            >
              {choicesWith(QUESTIONNAIRE_MINUTES, q.minutes).map((m) => (
                <option key={m} value={m}>
                  {m} minutes
                </option>
              ))}
            </select>
          ) : (
            <p className="text-body-sm text-ink-900">{q.minutes} minutes</p>
          )}
        </PanelSection>
        <div className="pb-1">
          <FlagToggle
            on={q.one_per_department}
            readOnly={!canEdit || busy}
            icon={<Users className="h-3 w-3" aria-hidden="true" />}
            onToggle={() => void patch({ one_per_department: !q.one_per_department })}
          >
            One response per department
          </FlagToggle>
        </div>
      </div>
      <PanelSection label={`Questions (${activeCount})`}>
        {questions.length === 0 ? <p className="text-meta text-ink-500">None yet</p> : null}
        {questions.map((x) =>
          canEdit ? (
            <QuestionRow key={x.id} question={x} areas={content.areas} busy={busy} write={write} />
          ) : (
            <ReadRow key={x.id} question={x} areas={content.areas} />
          ),
        )}
        {canEdit ? (
          <div className="mt-2">
            <InlineAdd
              trigger="chip"
              label="Question"
              inputLabel="New question"
              placeholder="The question"
              multiline
              busy={busy}
              onAdd={async (prompt) => {
                const { result, refusal } = await tryAdd(
                  write,
                  (id, base) => draftApi.addQuestionnaireQuestion(id, base, q.id, prompt),
                  q.code,
                  (err) => messageOf(err, 'The question could not be added.'),
                );
                if (result?.code) {
                  toast({ message: `${result.code} added. Set how it is answered and its area.` });
                }
                return refusal;
              }}
            />
          </div>
        ) : null}
        <FieldHelp>
          Numbers are assigned automatically and never reused, so every answer keeps pointing at its
          question. Answers already given keep the wording they were answered with.
        </FieldHelp>
      </PanelSection>
    </SidePanel>
  );
}

function areaChips(question: QuestionnaireQuestion, areas: readonly FrameworkArea[]) {
  if (question.area_ids.length === 0) return <AreaChip all />;
  return question.area_ids.map((id) => {
    const a = areas.find((x) => x.id === id);
    return (
      <AreaChip key={id} title={a?.name}>
        {a?.label ?? 'Unknown area'}
      </AreaChip>
    );
  });
}

function ReadRow({
  question,
  areas,
}: {
  question: QuestionnaireQuestion;
  areas: readonly FrameworkArea[];
}) {
  return (
    <div className="flex items-center gap-3 border-b border-ink-100 py-2 text-body-sm text-ink-900 last:border-b-0">
      <CodeTag code={question.code} />
      <span className="min-w-0 flex-1">{question.prompt}</span>
      <span className="flex-none text-meta text-ink-500">
        {answerTypeLabel(question.answer_type)}
      </span>
      {areaChips(question, areas)}
    </div>
  );
}

/**
 * One question in edit mode. A pick question needs two choices before it
 * can be saved as one (`question.options_required`), so choosing Pick one
 * or Pick several first asks for the choices, then saves both together.
 */
function QuestionRow({
  question,
  areas,
  busy,
  write,
}: {
  question: QuestionnaireQuestion;
  areas: readonly FrameworkArea[];
  busy: boolean;
  write: DraftWrite;
}) {
  const [pendingType, setPendingType] = React.useState<string | null>(null);
  const code = question.code;
  const patch = (p: QuestionPatch) =>
    write((id, base) => draftApi.patchQuestion(id, base, question.id, p), { what: code });

  if (question.status === 'retired') {
    return (
      <div className="flex items-center gap-2 border-b border-ink-100 py-2 text-body-sm">
        <CodeTag code={code} />
        <span className="min-w-0 flex-1 truncate text-ink-300 line-through">{question.prompt}</span>
        <WorkspaceButton
          variant="tertiary"
          disabled={busy}
          onClick={() => void patch({ status: 'active' })}
        >
          Restore {code}
        </WorkspaceButton>
      </div>
    );
  }

  const type = pendingType ?? question.answer_type;
  const areaValue =
    question.area_ids.length === 0
      ? ALL
      : question.area_ids.length === 1
        ? question.area_ids[0]!
        : SEVERAL;
  const pickable = areas.filter((a) => isActive(a) || question.area_ids.includes(a.id));
  return (
    <div className="border-b border-ink-100 py-2.5 last:border-b-0">
      <div className="mb-1.5 flex items-center gap-1.5">
        <CodeTag code={code} />
        <ChangeTag change={question.change} />
        <span className="flex-1" />
        <button
          type="button"
          aria-label={`Retire ${code}`}
          title="Retire"
          disabled={busy}
          onClick={() => void patch({ status: 'retired' })}
          className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-chip text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      <AutosaveField
        multiline
        required
        aria-label={`Wording of ${code}`}
        value={question.prompt}
        maxLength={2000}
        onSave={(prompt) => void patch({ prompt })}
      />
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        <select
          aria-label={`How ${code} is answered`}
          value={type}
          disabled={busy}
          onChange={(e) => {
            const next = e.target.value;
            if (isChoiceType(next) && question.options.length < 2) setPendingType(next);
            else {
              setPendingType(null);
              void patch({ answer_type: next });
            }
          }}
          className={SELECT_SM}
        >
          {ANSWER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <select
          aria-label={`Area of ${code}`}
          value={areaValue}
          disabled={busy}
          onChange={(e) => void patch({ area_ids: e.target.value === ALL ? [] : [e.target.value] })}
          className={SELECT_SM}
        >
          <option value={ALL}>All areas</option>
          {areaValue === SEVERAL ? (
            <option value={SEVERAL} disabled>
              {question.area_ids.length} areas
            </option>
          ) : null}
          {pickable.map((a) => (
            <option key={a.id} value={a.id}>
              {a.label}
            </option>
          ))}
        </select>
      </div>
      {isChoiceType(type) ? (
        <>
          <AutosaveField
            aria-label={`Choices of ${code}`}
            className="mt-1.5"
            value={question.options.join(', ')}
            placeholder="Choices, comma-separated"
            onSave={(text) => {
              const options = parseList(text);
              if (options.length < 2) return;
              void patch(pendingType ? { answer_type: pendingType, options } : { options }).then(
                (r) => {
                  if (r) setPendingType(null);
                },
              );
            }}
          />
          {pendingType ? (
            <FieldHelp>
              Add at least two choices, comma-separated. It becomes{' '}
              {answerTypeLabel(pendingType).toLowerCase()} once they are saved.
            </FieldHelp>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
